import { type Client } from '@microsoft/microsoft-graph-client';
import type {
  GroupOrMailbox,
  MailboxKind,
  MailboxVisibility,
  SPListItem,
} from '@/models/types';
import {
  getListItems,
  createListItem,
  updateListItem,
} from './sharePointService';
import { logChange } from './snapshotService';

// ─── Graph shapes ────────────────────────────────────────────────────────────

interface GraphGroup {
  id: string;
  displayName: string;
  mail?: string;
  description?: string;
  mailEnabled: boolean;
  securityEnabled: boolean;
  groupTypes: string[];
  visibility?: 'Public' | 'Private' | 'HiddenMembership';
}

interface GraphUser {
  id: string;
  displayName: string;
  userPrincipalName: string;
  mail?: string;
  accountEnabled: boolean;
}

export interface GroupMember {
  id: string;
  displayName: string;
  userPrincipalName?: string;
  mail?: string;
}

// ─── Fetchers ────────────────────────────────────────────────────────────────

async function fetchAllGroups(client: Client): Promise<GraphGroup[]> {
  const groups: GraphGroup[] = [];
  let response = await client
    .api('/groups')
    .select(
      'id,displayName,mail,description,mailEnabled,securityEnabled,groupTypes,visibility',
    )
    .top(500)
    .get();
  while (response) {
    groups.push(...(response.value ?? []));
    if (response['@odata.nextLink']) {
      response = await client.api(response['@odata.nextLink']).get();
    } else break;
  }
  return groups;
}

/**
 * Heuristic for shared mailbox candidates: disabled accounts that have a mailbox.
 * Exchange admin APIs give the authoritative list, but those need Exchange roles.
 */
async function fetchSharedMailboxCandidates(
  client: Client,
): Promise<GraphUser[]> {
  try {
    const resp = await client
      .api('/users')
      .filter('accountEnabled eq false')
      .select('id,displayName,userPrincipalName,mail,accountEnabled')
      .top(500)
      .get();
    return (resp.value ?? []).filter((u: GraphUser) => !!u.mail);
  } catch {
    return [];
  }
}

function classify(g: GraphGroup): MailboxKind | null {
  if (g.groupTypes?.includes('Unified')) return 'M365Group';
  if (g.mailEnabled && !g.securityEnabled) return 'DistributionList';
  if (g.securityEnabled) return 'SecurityGroup';
  return null;
}

function normalizeVisibility(v?: string): MailboxVisibility {
  if (v === 'HiddenMembership') return 'Hidden';
  if (v === 'Private') return 'Private';
  return 'Public';
}

export async function fetchGroupMembers(
  client: Client,
  groupId: string,
): Promise<GroupMember[]> {
  const members: GroupMember[] = [];
  let resp = await client
    .api(`/groups/${groupId}/members`)
    .select('id,displayName,userPrincipalName,mail')
    .top(200)
    .get();
  while (resp) {
    members.push(
      ...(resp.value ?? []).map((m: Partial<GroupMember>) => ({
        id: m.id ?? '',
        displayName: m.displayName ?? m.userPrincipalName ?? m.mail ?? '',
        userPrincipalName: m.userPrincipalName,
        mail: m.mail,
      })),
    );
    if (resp['@odata.nextLink']) {
      resp = await client.api(resp['@odata.nextLink']).get();
    } else break;
  }
  return members;
}

// ─── Write operations (with audit) ───────────────────────────────────────────

export async function addGroupMember(
  client: Client,
  groupId: string,
  userObjectId: string,
  actorEmail: string,
  memberDisplayName: string,
  groupDisplayName: string,
): Promise<void> {
  await client.api(`/groups/${groupId}/members/$ref`).post({
    '@odata.id': `https://graph.microsoft.com/v1.0/directoryObjects/${userObjectId}`,
  });
  await logChange(
    client,
    'Assigned',
    'GroupMember',
    groupId,
    groupDisplayName,
    `Added ${memberDisplayName} to ${groupDisplayName}`,
    actorEmail,
  );
}

export async function removeGroupMember(
  client: Client,
  groupId: string,
  userObjectId: string,
  actorEmail: string,
  memberDisplayName: string,
  groupDisplayName: string,
): Promise<void> {
  await client.api(`/groups/${groupId}/members/${userObjectId}/$ref`).delete();
  await logChange(
    client,
    'Revoked',
    'GroupMember',
    groupId,
    groupDisplayName,
    `Removed ${memberDisplayName} from ${groupDisplayName}`,
    actorEmail,
  );
}

export async function createDistributionList(
  client: Client,
  displayName: string,
  mailNickname: string,
  description: string,
  actorEmail: string,
): Promise<string> {
  const resp = await client.api('/groups').post({
    displayName,
    mailNickname,
    description,
    mailEnabled: true,
    securityEnabled: false,
    groupTypes: [],
  });
  await logChange(
    client,
    'Created',
    'Group',
    resp.id,
    displayName,
    `Distribution list created`,
    actorEmail,
  );
  return resp.id;
}

export async function createM365Group(
  client: Client,
  displayName: string,
  mailNickname: string,
  description: string,
  actorEmail: string,
): Promise<string> {
  const resp = await client.api('/groups').post({
    displayName,
    mailNickname,
    description,
    mailEnabled: true,
    securityEnabled: false,
    groupTypes: ['Unified'],
    visibility: 'Private',
  });
  await logChange(
    client,
    'Created',
    'Group',
    resp.id,
    displayName,
    `M365 group created`,
    actorEmail,
  );
  return resp.id;
}

export async function deleteGroup(
  client: Client,
  groupId: string,
  displayName: string,
  actorEmail: string,
): Promise<void> {
  await client.api(`/groups/${groupId}`).delete();
  await logChange(
    client,
    'Deleted',
    'Group',
    groupId,
    displayName,
    `Group deleted`,
    actorEmail,
  );
}

// ─── Sync ────────────────────────────────────────────────────────────────────

export interface MailboxSyncResult {
  groupsProcessed: number;
  sharedMailboxesProcessed: number;
  created: number;
  updated: number;
  deactivated: number;
}

export async function syncMailboxesAndGroups(
  client: Client,
  actorEmail: string,
): Promise<MailboxSyncResult> {
  const [graphGroups, sharedMailboxes] = await Promise.all([
    fetchAllGroups(client),
    fetchSharedMailboxCandidates(client),
  ]);

  const existing = await getListItems<GroupOrMailbox>(
    client,
    'OC_GroupsAndMailboxes',
  );
  const byId = new Map<string, SPListItem<GroupOrMailbox>>();
  for (const e of existing) byId.set(e.fields.EntraObjectId, e);

  const result: MailboxSyncResult = {
    groupsProcessed: 0,
    sharedMailboxesProcessed: 0,
    created: 0,
    updated: 0,
    deactivated: 0,
  };

  const now = new Date().toISOString();
  const seen = new Set<string>();

  for (const g of graphGroups) {
    const kind = classify(g);
    if (!kind) continue;
    result.groupsProcessed++;
    seen.add(g.id);

    const record: Partial<GroupOrMailbox> = {
      Title: g.displayName,
      EntraObjectId: g.id,
      Email: g.mail ?? '',
      Kind: kind,
      MemberCount: 0, // fetched on-demand
      Visibility: normalizeVisibility(g.visibility),
      Description: g.description ?? '',
      LastSyncedAt: now,
      IsActive: true,
    };

    const cur = byId.get(g.id);
    if (cur) {
      if (
        cur.fields.Title !== record.Title ||
        cur.fields.Email !== record.Email ||
        cur.fields.Kind !== record.Kind ||
        !cur.fields.IsActive
      ) {
        await updateListItem<GroupOrMailbox>(
          client,
          'OC_GroupsAndMailboxes',
          cur.id,
          record,
        );
        result.updated++;
      }
    } else {
      await createListItem<GroupOrMailbox>(
        client,
        'OC_GroupsAndMailboxes',
        record,
      );
      result.created++;
    }
  }

  for (const u of sharedMailboxes) {
    if (!u.mail) continue;
    result.sharedMailboxesProcessed++;
    seen.add(u.id);

    const record: Partial<GroupOrMailbox> = {
      Title: u.displayName,
      EntraObjectId: u.id,
      Email: u.mail,
      Kind: 'SharedMailbox',
      MemberCount: 0,
      Visibility: 'Private',
      Description: 'Auto-detected shared mailbox candidate',
      LastSyncedAt: now,
      IsActive: true,
    };

    const cur = byId.get(u.id);
    if (cur) {
      if (!cur.fields.IsActive) {
        await updateListItem<GroupOrMailbox>(
          client,
          'OC_GroupsAndMailboxes',
          cur.id,
          record,
        );
        result.updated++;
      }
    } else {
      await createListItem<GroupOrMailbox>(
        client,
        'OC_GroupsAndMailboxes',
        record,
      );
      result.created++;
    }
  }

  // Deactivate entries no longer present
  for (const e of existing) {
    if (!seen.has(e.fields.EntraObjectId) && e.fields.IsActive) {
      await updateListItem<GroupOrMailbox>(
        client,
        'OC_GroupsAndMailboxes',
        e.id,
        { IsActive: false },
      );
      result.deactivated++;
    }
  }

  await logChange(
    client,
    'Synced',
    'Mailboxes',
    '',
    'Entra Groups + Mailboxes Sync',
    `${result.groupsProcessed} groups, ${result.sharedMailboxesProcessed} mailboxes, ${result.created} new, ${result.updated} updated, ${result.deactivated} deactivated`,
    actorEmail,
  );

  return result;
}
